import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#announceOnOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#announceOnOpen);
  }

  /**
   * Announces the cart summary to assistive technology when the drawer opens.
   * Reads a server-rendered, live-data string ([data-cart-announcement]) from inside the
   * drawer (re-rendered on every cart morph) and pushes it to the polite live region:
   * "Your Bag opened. N items. Subtotal $X." No hardcoded values.
   */
  #announceOnOpen = () => {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    const source = this.querySelector('[data-cart-announcement]');
    const message = source?.textContent?.trim();
    if (!liveRegion || !message) return;

    // Clear then set on the next frame so the status region re-announces even if unchanged.
    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   *
   * `data.silent` opts a source out of auto-open. Surfaces that edit the cart IN PLACE — the
   * Collection Builder's +/− steppers, for example — are not add-to-cart moments; popping the
   * drawer over them on every tap would bury the interface the shopper is using.
   *
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { silent?: boolean } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open') && !event.detail?.data?.silent) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * ADA: <dialog>.showModal() programmatically focuses the first focusable child (the
     * skip-to-checkout link, or the close button on an empty cart). iOS treats that as
     * keyboard focus and paints the reveal/focus ring over the header. Redirect focus to
     * the inert card wrapper (tabindex="-1") so nothing shows a ring on touch open;
     * keyboard users Tab from here into the controls, which keep their :focus-visible.
     */
    const inner = /** @type {HTMLElement | null} */ (this.refs.dialog?.querySelector('.cart-drawer__inner'));
    if (inner) {
      requestAnimationFrame(() => inner.focus({ preventScroll: true }));
    }
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
